/* The Lineage Journal — article template behavior (CNV-4266)
 *
 * <journal-toc> — "In this article" list. Highlights the entry whose section
 * is currently in view. Markup: <journal-toc><ol><li><a href="#anchor">…
 * The section headings carry the ids; scroll-margin-top handles the sticky
 * header offset, so this component only manages the .is-active class.
 *
 * <journal-newsletter-form> — wraps the signup form and subscribes the email
 * via Klaviyo's client API. Reads data-company-id / data-list-id (both come
 * from theme settings, "Journal (blog)" group); while the list ID is empty
 * the form stays a quiet no-op so the design works before the account is
 * connected.
 */

class JournalToc extends HTMLElement {
  connectedCallback() {
    this.links = Array.from(this.querySelectorAll('a[href^="#"]'));
    if (!this.links.length || !('IntersectionObserver' in window)) return;

    this.targets = this.links
      .map((link) => document.getElementById(link.getAttribute('href').slice(1)))
      .filter(Boolean);
    if (!this.targets.length) return;

    // Track which heading is nearest above the reading line.
    this.observer = new IntersectionObserver(() => this.update(), {
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0,
    });
    this.targets.forEach((t) => this.observer.observe(t));
    this.update();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  update() {
    const line = window.innerHeight * 0.25;
    let current = this.targets[0];
    for (const target of this.targets) {
      if (target.getBoundingClientRect().top <= line) current = target;
    }
    this.links.forEach((link) => {
      const active = link.getAttribute('href') === '#' + current.id;
      link.parentElement.classList.toggle('is-active', active);
      if (active) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    });
  }
}

class JournalNewsletterForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  message(text, isError) {
    let el = this.querySelector('.journal__form-msg');
    if (!el) {
      el = document.createElement('p');
      el.className = 'journal__form-msg';
      el.setAttribute('role', 'status');
      this.append(el);
    }
    el.textContent = text;
    el.classList.toggle('journal__form-msg--error', Boolean(isError));
  }

  async handleSubmit(event) {
    event.preventDefault();
    const companyId = this.getAttribute('data-company-id');
    const listId = this.getAttribute('data-list-id');
    if (!companyId || !listId) return; // Klaviyo not connected yet

    const input = this.form.querySelector('input[type="email"]');
    if (!input || !input.checkValidity()) {
      this.message(this.getAttribute('data-invalid-text') || 'Please enter a valid email address.', true);
      if (input) input.focus();
      return;
    }

    const button = this.form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;

    try {
      const response = await fetch(
        'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Journal newsletter',
                profile: { data: { type: 'profile', attributes: { email: input.value.trim() } } },
              },
              relationships: { list: { data: { type: 'list', id: listId } } },
            },
          }),
        }
      );
      if (!response.ok) throw new Error('Klaviyo responded ' + response.status);
      this.form.hidden = true;
      this.message(this.getAttribute('data-success-text') || "You're in — check your inbox.");
    } catch (error) {
      if (button) button.disabled = false;
      this.message(this.getAttribute('data-error-text') || 'Something went wrong — please try again.', true);
    }
  }
}

/* <journal-load-more> — wraps an outline button; each click reveals the next
 * batch of `.is-hidden` items inside data-target, hiding the button when none
 * remain. Optional: data-count-el (selector) + data-total + data-count-suffix
 * keep a "Showing X of N articles" line up to date. */
class JournalLoadMore extends HTMLElement {
  connectedCallback() {
    this.btn = this.querySelector('button');
    this.target = document.querySelector(this.getAttribute('data-target'));
    this.batch = parseInt(this.getAttribute('data-batch') || '6', 10);
    const countSel = this.getAttribute('data-count-el');
    this.countEl = countSel ? document.querySelector(countSel) : null;
    this.total = parseInt(this.getAttribute('data-total') || '0', 10);
    if (!this.btn || !this.target) return;
    this.btn.addEventListener('click', () => this.reveal());
    this.sync();
  }

  hidden() {
    return this.target.querySelectorAll('.is-hidden');
  }

  reveal() {
    const items = this.hidden();
    const n = Math.min(this.batch, items.length);
    for (let i = 0; i < n; i++) items[i].classList.remove('is-hidden');
    this.sync();
  }

  sync() {
    const remaining = this.hidden().length;
    if (!remaining) this.style.display = 'none';
    if (this.countEl && this.total) {
      const suffix = this.getAttribute('data-count-suffix') || 'articles';
      this.countEl.textContent = 'Showing ' + (this.total - remaining) + ' of ' + this.total + ' ' + suffix;
    }
  }
}

if (!customElements.get('journal-toc')) {
  customElements.define('journal-toc', JournalToc);
}
if (!customElements.get('journal-newsletter-form')) {
  customElements.define('journal-newsletter-form', JournalNewsletterForm);
}
if (!customElements.get('journal-load-more')) {
  customElements.define('journal-load-more', JournalLoadMore);
}

/* Recipe tick lists — ingredients and tools behave like the checkboxes every recipe
   site has: tap to strike an item off while you cook. The markup comes out of a rich
   text field, so there is nothing to hook into server-side; the list items are promoted
   to checkboxes here and stay plain readable text if this never runs. State is
   deliberately not persisted — it is a cooking scratchpad, not a saved document. */
function journalInitTickLists(root) {
  const lists = (root || document).querySelectorAll('.journal__recipe-checks li');
  lists.forEach((li) => {
    if (li.dataset.tickable) return;
    li.dataset.tickable = '1';
    li.setAttribute('role', 'checkbox');
    li.setAttribute('aria-checked', 'false');
    li.setAttribute('tabindex', '0');

    const toggle = () => {
      const on = li.getAttribute('aria-checked') === 'true';
      li.setAttribute('aria-checked', on ? 'false' : 'true');
    };

    li.addEventListener('click', (e) => {
      // let a linked ingredient still open its product page
      if (e.target.closest('a')) return;
      toggle();
    });

    li.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        if (e.target.closest('a') && e.key === 'Enter') return;
        e.preventDefault();
        toggle();
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => journalInitTickLists());
} else {
  journalInitTickLists();
}
